import { useState } from 'react'
import { Link, router, usePage } from '@inertiajs/react'
import { MapPin } from 'lucide-react'
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import type { NetworkPerson, SharedProps } from '@/types'

function displayName(person: NetworkPerson): string {
  if (person.first_name && person.last_name) return `${person.first_name} ${person.last_name}`
  return `@${person.username}`
}

function initials(person: NetworkPerson): string {
  if (person.first_name && person.last_name) return `${person.first_name[0]}${person.last_name[0]}`
  return person.username[0]?.toUpperCase() ?? '?'
}

export default function PersonCard({ person }: { person: NetworkPerson }) {
  const { current_user } = usePage<SharedProps>().props
  const [confirmOpen, setConfirmOpen] = useState(false)
  const currentUserHasPrivateProfile = current_user?.visibility === false
  const isSelf = current_user?.id === person.id
  const location = [person.city, person.state].filter(Boolean).join(', ')

  function follow(confirmed = false) {
    router.post(
      `/u/${person.username}/follow`,
      confirmed ? { confirmed_private_profile_visibility: true } : {},
      { preserveScroll: true, preserveState: true, onSuccess: () => setConfirmOpen(false) }
    )
  }

  function handleFollowClick(e: React.MouseEvent) {
    e.preventDefault()
    e.stopPropagation()
    if (person.is_following) {
      router.delete(`/u/${person.username}/follow`, { preserveScroll: true, preserveState: true })
    } else if (currentUserHasPrivateProfile) {
      setConfirmOpen(true)
    } else {
      follow()
    }
  }

  return (
    <Link
      href={`/u/${person.username}`}
      className="group flex h-full flex-col rounded-2xl bg-card p-5 shadow-sm ring-1 ring-black/5 transition-shadow hover:shadow-lg hover:ring-primary/20"
    >
      <div className="flex items-start gap-3">
        <Avatar className="size-12">
          {person.avatar_url && <AvatarImage src={person.avatar_url} alt={displayName(person)} />}
          <AvatarFallback>{initials(person)}</AvatarFallback>
        </Avatar>
        <div className="min-w-0 flex-1">
          <p className="truncate font-semibold">{displayName(person)}</p>
          <p className="truncate text-sm text-muted-foreground">@{person.username}</p>
        </div>
        {person.business && <Badge variant="secondary" className="shrink-0 text-xs">Business</Badge>}
      </div>

      {person.bio && (
        <p className="mt-3 line-clamp-2 text-sm text-foreground text-pretty">{person.bio}</p>
      )}

      {location && (
        <p className="mt-2 flex items-center gap-1 text-sm text-muted-foreground">
          <MapPin className="size-3.5" />
          {location}
        </p>
      )}

      {person.interested_causes.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {person.interested_causes.map((cause) => (
            <Badge key={cause} variant="secondary" className="text-xs">{cause}</Badge>
          ))}
        </div>
      )}

      {!isSelf && (
        <div className="mt-4 pt-1">
          <Button
            variant={person.is_following ? 'default' : 'outline'}
            size="sm"
            className="w-full"
            onClick={handleFollowClick}
          >
            {person.is_following ? 'Following' : 'Follow'}
          </Button>
          <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
            <DialogContent className="sm:max-w-md" onClick={(e) => e.preventDefault()}>
              <DialogHeader>
                <DialogTitle>Follow with a private profile?</DialogTitle>
                <DialogDescription>
                  Your profile is private, so @{person.username} will receive a notification that you followed them,
                  but they will not be able to view your profile. Do you want to proceed?
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button variant="outline" onClick={(e) => { e.preventDefault(); setConfirmOpen(false) }}>Cancel</Button>
                <Button onClick={(e) => { e.preventDefault(); follow(true) }}>Proceed</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      )}
    </Link>
  )
}
